import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Barcode,
  Boxes,
  MinusCircle,
  MoreHorizontal,
  PackagePlus,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import { PERMISSIONS, type StockLevel } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { DropdownMenu } from '@/components/ui/DropdownMenu';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { ProductThumb } from '@/modules/catalog/components/ProductThumb';
import type { LabelBuilderState } from '@/modules/catalog/labels/labelLines';
import { useStockQuery } from '../api/inventory.api';
import { StockMovementDrawer, type MovementMode } from '../components/StockMovementDrawer';
import { StockStateBadge } from '../components/StockStateBadge';

const PAGE_SIZE = 20;

type StockFilter = '' | 'low' | 'out';

export function StockPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { activeBranch } = useBranch();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StockFilter>('');
  const [page, setPage] = useState(1);
  const [movement, setMovement] = useState<{ mode: MovementMode; level: StockLevel } | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const deferredSearch = useDeferredValue(search);

  const { data, isPending, isError, error, refetch, isFetching } = useStockQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    lowStockOnly: filter === 'low',
    outOfStockOnly: filter === 'out',
  });

  const canReceive = can(PERMISSIONS.INVENTORY_RECEIVE);
  const canPrintLabels = can(PERMISSIONS.CATALOG_VIEW);
  const navigate = useNavigate();

  const printLabels = (level: StockLevel) => {
    const state: LabelBuilderState = {
      lines: [
        {
          variantId: level.variantId,
          sku: level.sku,
          productName: level.productName,
          productNameAr: null,
          variantName: level.variantName,
          quantity: '1',
        },
      ],
    };
    void navigate('/inventory/labels', { state });
  };
  const canAdjust = can(PERMISSIONS.INVENTORY_ADJUST);

  // The same permitted actions as the table's buttons, for the phone card's menu.
  const cardActions = (level: StockLevel): CardAction[] => [
    ...(canPrintLabels
      ? [
          {
            key: 'print',
            label: t('catalog.labels.printBarcode'),
            icon: Barcode,
            run: () => printLabels(level),
          },
        ]
      : []),
    ...(canReceive
      ? [
          {
            key: 'receive',
            label: t('inventory.movement.receive'),
            icon: PackagePlus,
            run: () => setMovement({ mode: 'receive', level }),
          },
        ]
      : []),
    ...(canAdjust
      ? [
          {
            key: 'issue',
            label: t('inventory.movement.issue'),
            icon: MinusCircle,
            run: () => setMovement({ mode: 'issue', level }),
          },
          {
            key: 'adjust',
            label: t('inventory.movement.adjust'),
            icon: SlidersHorizontal,
            run: () => setMovement({ mode: 'adjust', level }),
          },
        ]
      : []),
  ];

  const filterOptions = [
    { value: '', label: t('inventory.filters.all') },
    { value: 'low', label: t('inventory.filters.lowStock') },
    { value: 'out', label: t('inventory.filters.outOfStock') },
  ];

  const columns: Column<StockLevel>[] = [
    {
      key: 'product',
      header: t('inventory.fields.product'),
      primary: true,
      render: (level) => (
        <span className="flex items-center gap-3">
          <ProductThumb image={level.image} alt={level.variantName ?? level.productName} />
          <span className="flex min-w-0 flex-col">
            <span className="text-ink font-medium">{level.productName}</span>
            <span className="text-muted text-xs">
              <span className="font-mono" dir="ltr">
                {level.sku}
              </span>
              {level.variantName ? <span className="mx-1.5">· {level.variantName}</span> : null}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'onHand',
      header: t('inventory.fields.onHand'),
      align: 'end',
      render: (level) => (
        <span className="text-ink font-semibold" dir="ltr">
          {level.quantityOnHand}
        </span>
      ),
    },
    {
      key: 'reorderPoint',
      header: t('inventory.fields.reorderPoint'),
      align: 'end',
      render: (level) => (
        <span className="text-muted" dir="ltr">
          {level.reorderPoint ?? '—'}
        </span>
      ),
    },
    {
      key: 'batch',
      header: t('inventory.fields.batchTracked'),
      render: (level) => (
        <span className="text-muted text-xs">
          {level.isBatchTracked ? t('common.yes') : t('common.no')}
        </span>
      ),
    },
    {
      key: 'state',
      header: t('inventory.fields.state'),
      render: (level) => <StockStateBadge level={level} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      hideOnCard: true,
      render: (level) => (
        <span className="inline-flex items-center gap-1">
          {canPrintLabels ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => printLabels(level)}
              aria-label={t('catalog.labels.printFor', { sku: level.sku })}
            >
              <Barcode className="size-4" aria-hidden />
              <span className="hidden xl:inline">{t('catalog.labels.printBarcode')}</span>
            </Button>
          ) : null}

          {canReceive ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMovement({ mode: 'receive', level })}
              aria-label={t('inventory.movement.receiveNamed', { sku: level.sku })}
            >
              <PackagePlus className="size-4" aria-hidden />
              <span className="hidden xl:inline">{t('inventory.movement.receive')}</span>
            </Button>
          ) : null}

          {canAdjust ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMovement({ mode: 'issue', level })}
                aria-label={t('inventory.movement.issueNamed', { sku: level.sku })}
              >
                <MinusCircle className="size-4" aria-hidden />
                <span className="hidden xl:inline">{t('inventory.movement.issue')}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMovement({ mode: 'adjust', level })}
                aria-label={t('inventory.movement.adjustNamed', { sku: level.sku })}
              >
                <SlidersHorizontal className="size-4" aria-hidden />
                <span className="hidden xl:inline">{t('inventory.movement.adjust')}</span>
              </Button>
            </>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('inventory.stock.title')}
        subtitle={
          activeBranch
            ? t('inventory.stock.subtitleBranch', { branch: activeBranch.name })
            : t('inventory.stock.subtitle')
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}

      {/* Stock is per branch, so the page is meaningless without one selected. */}
      {!activeBranch ? (
        <Alert tone="warning">{t('inventory.stock.selectBranch')}</Alert>
      ) : (
        <Card>
          <CardHeader
            title={t('inventory.stock.title')}
            subtitle={
              data ? t('inventory.stock.count', { count: data.pagination.totalItems }) : undefined
            }
            action={
              <div className="flex flex-wrap items-center gap-2">
                <div className="w-40 sm:w-52">
                  <Input
                    type="search"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                    placeholder={t('inventory.stock.searchPlaceholder')}
                    aria-label={t('inventory.stock.searchLabel')}
                  />
                </div>
                <div className="w-36">
                  <Select
                    options={filterOptions}
                    value={filter}
                    aria-label={t('inventory.fields.state')}
                    onChange={(event) => {
                      setFilter(event.target.value as StockFilter);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
            }
          />

          <DataTable
            caption={t('inventory.stock.tableCaption')}
            columns={columns}
            rows={data?.items}
            rowKey={(level) => level.id}
            cardActions={(level) => <CardActionsMenu level={level} actions={cardActions(level)} />}
            isLoading={isPending}
            isError={isError}
            errorMessage={apiErrorMessage(error) ?? t('inventory.stock.loadError')}
            onRetry={() => void refetch()}
            emptyIcon={Boxes}
            emptyTitle={
              deferredSearch || filter ? t('inventory.stock.noMatches') : t('inventory.stock.empty')
            }
            emptyDescription={
              deferredSearch || filter
                ? t('inventory.stock.noMatchesHint')
                : t('inventory.stock.emptyHint')
            }
          />

          {data ? (
            <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
          ) : null}
        </Card>
      )}

      <StockMovementDrawer
        mode={movement?.mode ?? null}
        level={movement?.level ?? null}
        onClose={() => setMovement(null)}
        onDone={setFeedback}
      />
    </div>
  );
}

interface CardAction {
  key: string;
  label: string;
  icon: LucideIcon;
  run: () => void;
}

/** One compact menu instead of four buttons, so a stock card stays readable on a phone; nothing at all when no action is permitted. */
function CardActionsMenu({ level, actions }: { level: StockLevel; actions: CardAction[] }) {
  const { t } = useTranslation();
  if (actions.length === 0) return null;

  return (
    <DropdownMenu
      label={t('inventory.stock.actionsFor', { sku: level.sku })}
      triggerClassName="border-line text-ink hover:bg-canvas border px-3 py-1.5"
      panelClassName="w-52"
      trigger={
        <>
          <MoreHorizontal className="size-4" aria-hidden />
          {t('common.actions')}
        </>
      }
    >
      {(close) =>
        actions.map((action) => (
          <button
            key={action.key}
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              action.run();
            }}
            className="hover:bg-canvas text-ink flex w-full items-center gap-2 px-3 py-2.5 text-start text-sm"
          >
            <action.icon className="size-4 shrink-0" aria-hidden />
            {action.label}
          </button>
        ))
      }
    </DropdownMenu>
  );
}
